import { SignalingClient } from './signaling.js';
import { WebRTCManager } from './webrtc.js';

const STORAGE_KEY = 'active_session';
const PREV_SESSION_KEY = 'previous_session';
const DEFAULT_ACTIVE_USERS = 20;

export class App {
  constructor({
    signaling = new SignalingClient(),
    webrtc = new WebRTCManager(),
    root = document.getElementById('app'),
    storage = window.localStorage,
    idFactory = () => crypto.randomUUID(),
    clock = { now: () => Date.now() },
    timers = window,
  } = {}) {
    this.signaling = signaling;
    this.webrtc = webrtc;
    this.root = root;
    this.storage = storage;
    this.idFactory = idFactory;
    this.clock = clock;
    this.timers = timers;

    this.sessionId = null;
    this.peerSessionId = null;
    this.state = 'IDLE';
    this.screen = 'home';
    this.matchPhase = 'searching';
    this.isMuted = false;
    this.isSpeaking = false;
    this.isDark = false;
    this.activeUsers = DEFAULT_ACTIVE_USERS;
    this.callStartedAt = null;
    this.callSeconds = 0;
    this.skipArmed = false;
    this.errorMessage = '';
    this.leaving = false;
    this.mediaLive = false;
    this.lastIsDesktop = this.isDesktop();
    this.peerReconnectingAt = null;
    this.reconnectGracePeriodMs = 800;
    this.reconnectFallbackTimer = null;
    this.matchEnterTimer = null;
    this.skipResetTimer = null;
    this.speakingTimer = null;
    this.callTimer = null;
    this.errorTimer = null;

    this.initListeners();
    this.render();
  }

  initListeners() {
    this.signaling.on('MATCH_FOUND', (message) => this.handleMatchFound(message));
    this.signaling.on('OFFER', (message) => this.handleOffer(message));
    this.signaling.on('ANSWER', (message) => this.handleAnswer(message));
    this.signaling.on('ICE_CANDIDATE', (message) => this.handleIceCandidate(message));
    this.signaling.on('PEER_LEFT', () => this.handlePeerLeft());
    this.signaling.on('PEER_RECONNECTING', () => this.handlePeerReconnecting());
    this.signaling.on('SESSION_RESUME', () => this.handleSessionResume());
    this.signaling.on('TIMEOUT', () => this.handleTimeout());
    this.signaling.on('ACTIVE_USERS', (message) => this.updateActiveUsers(message.count));
    this.signaling.on('ERROR', (message) => this.showError(message.message));
    this.signaling.on('close', () => this.handleDisconnect());

    this.webrtc.onIceCandidate = (candidate) => this.signaling.sendIceCandidate(candidate);
    this.webrtc.onRemoteStream = (stream) => {
      this.playRemoteAudio(stream);
      this.markCallLive();
    };
    this.webrtc.onConnectionState = (state) => this.handleConnectionState(state);
    this.webrtc.onIceRestart = (offer) => this.signaling.sendOffer(offer);

    window.addEventListener('beforeunload', () => this.saveSessionForReconnect());
    window.addEventListener('resize', () => {
      const desktop = this.isDesktop();
      if (desktop === this.lastIsDesktop) return;
      this.lastIsDesktop = desktop;
      this.render();
    });
  }

  render() {
    document.documentElement.dataset.theme = this.isDark ? 'dark' : 'light';
    this.root.innerHTML = `
      <div class="app-shell">
        ${this.isDesktop() ? this.renderDesktopNav() : ''}
        ${this.screen === 'home' ? this.renderHome() : ''}
        ${this.screen === 'matching' ? this.renderMatching() : ''}
        ${this.screen === 'active' ? this.renderActive() : ''}
        ${this.errorMessage ? `<div class="error-toast">${this.escape(this.errorMessage)}</div>` : ''}
      </div>
    `;
    this.bindEvents();
  }

  renderDesktopNav() {
    return `
      <nav class="top-nav desktop-only">
        <h1 class="brand">Ping Talk</h1>
        <div class="nav-count">${this.renderDot()}<span><span data-online-count>${this.formatCount()}</span> online right now</span></div>
        <button class="theme-toggle" data-action="toggle-theme">${this.isDark ? 'Light' : 'Dark'}</button>
      </nav>
    `;
  }

  renderMobileHeader({ active = false, settings = true } = {}) {
    const left = active
      ? `<div class="user-count">${this.renderDot()}<span data-online-count>${this.formatCount()}</span></div>`
      : '<div></div>';
    return `
      <header class="mobile-header">
        ${left}
        <h1 class="brand">Ping Talk</h1>
        ${settings ? '<button class="icon-button" data-action="toggle-theme" aria-label="Toggle color mode">⚙</button>' : '<div></div>'}
      </header>
    `;
  }

  renderHome() {
    const greeting = this.getGreeting();
    return `
      ${this.isDesktop() ? '' : this.renderMobileHeader()}
      <section class="screen home">
        <div class="center-stack">
          <h2 class="heading">${greeting}<br>Ready to chat?</h2>
          <p class="subtitle">Connect with a random stranger — voice first.</p>
          ${this.renderOrb({ speaking: false, matching: false })}
          <button class="cta" data-action="join">Say Hello 👋</button>
        </div>
      </section>
    `;
  }

  renderMatching() {
    const found = this.matchPhase === 'found';
    return `
      ${this.isDesktop() ? '' : this.renderMobileHeader({ settings: false })}
      <section class="screen">
        <div class="center-stack">
          ${this.renderOrb({ speaking: found, matching: !found })}
          ${found
            ? '<div class="found-pill">Match found! Connecting…</div>'
            : `<h2 class="match-title">Finding someone<span id="dots">${this.matchDots()}</span></h2>`}
          <p class="eyebrow">Looking across <span data-online-count>${this.formatCount()}</span> voices</p>
          <button class="ghost" data-action="cancel">Cancel</button>
        </div>
      </section>
    `;
  }

  renderActive() {
    const speaking = this.isSpeaking && !this.isMuted;
    return `
      ${this.isDesktop()
        ? `<div class="top-nav desktop-only"><div class="user-count">${this.renderDot()}<span data-online-count>${this.formatCount()}</span></div><h1 class="brand">Ping Talk</h1>${this.renderTimer()}</div>`
        : this.renderMobileHeader({ active: true })}
      <section class="screen active-screen">
        ${!this.isDesktop() ? this.renderTimer() : ''}
        <div class="active-core">
          ${this.renderOrb({ speaking, matching: false })}
          <div class="wave-bars ${speaking ? 'active' : ''}">
            ${Array.from({ length: 7 }, () => '<span></span>').join('')}
          </div>
          <div class="partner">
            <div class="connection-state">${this.renderDot()}<span data-connection-label>Active voice connection</span></div>
          </div>
        </div>
        <div class="controls-area">
          ${this.skipArmed ? '<div class="tooltip">Tap again to find new match</div>' : ''}
          <div class="controls">
            <button class="control ${this.isMuted ? 'muted' : ''}" data-action="mute">
              <span class="control-icon">${this.isMuted ? '🔇' : '🎙'}</span>
              <span data-mute-label>${this.isMuted ? 'Unmute' : 'Mute'}</span>
            </button>
            <button class="control ${this.skipArmed ? 'skip-armed' : ''}" data-action="new-match">
              <span class="control-icon">⏭</span>
              <span>New Match</span>
            </button>
            <button class="control leave" data-action="leave">
              <span class="control-icon">📵</span>
              <span>Leave</span>
            </button>
          </div>
        </div>
      </section>
    `;
  }

  renderOrb({ speaking, matching }) {
    return `
      <div class="orb-wrap ${speaking ? 'speaking' : ''}" style="--orb-size:${this.isDesktop() ? 200 : 180}px">
        ${matching ? '<div class="match-ring"></div><div class="match-ring"></div><div class="match-ring"></div>' : ''}
        <div class="orb-glow"></div>
        <div class="orb"></div>
      </div>
    `;
  }

  renderTimer() {
    return `<div class="timer-chip">${this.formatTime(this.callSeconds)}</div>`;
  }

  renderDot() {
    return '<span class="dot" aria-hidden="true"></span>';
  }

  bindEvents() {
    this.root.querySelectorAll('[data-action]').forEach((el) => {
      el.addEventListener('click', (event) => {
        const action = event.currentTarget.dataset.action;
        if (action === 'join') this.joinQueue();
        if (action === 'cancel') this.leave();
        if (action === 'leave') this.leave();
        if (action === 'mute') this.toggleMute();
        if (action === 'new-match') this.handleNewMatchTap();
        if (action === 'toggle-theme') this.toggleTheme();
      });
    });
  }

  async joinQueue() {
    try {
      this.leaving = false;
      this.mediaLive = false;
      this.sessionId = this.idFactory();
      this.setSessionLock();
      this.state = 'QUEUED';
      this.screen = 'matching';
      this.matchPhase = 'searching';
      this.callStartedAt = null;
      this.callSeconds = 0;
      this.render();

      await this.ensureSignaling();
      this.signaling.joinQueue();
    } catch (error) {
      console.error('Failed to join queue:', error);
      this.showError('Failed to connect. Please try again.');
      this.resetToHome();
    }
  }

  async ensureSignaling() {
    if (this.signaling.isOpen?.() || (this.signaling.ws && this.signaling.ws.readyState === 1)) {
      this.signaling.sessionId = this.sessionId;
      return;
    }
    await this.signaling.connect(this.sessionId);
  }

  async handleMatchFound(message) {
    this.peerSessionId = message.peerSessionId;
    this.state = 'MATCHED';
    this.matchPhase = 'found';
    this.screen = 'matching';
    this.mediaLive = false;
    this.callStartedAt = null;
    this.callSeconds = 0;
    this.clearCallTimers();
    this.render();

    try {
      await this.webrtc.initLocalStream();
      this.applyMuteStateToStream();

      if (message.initiator) {
        const offer = await this.webrtc.createOffer();
        this.signaling.sendOffer(offer);
      }
    } catch (error) {
      console.error('Failed to start call:', error);
      this.showError('Failed to start audio. Please check microphone permissions.');
      this.leave();
    }
  }

  async handleOffer(message) {
    try {
      await this.webrtc.initLocalStream();
      this.applyMuteStateToStream();
      const answer = await this.webrtc.handleOffer(message.data);
      this.signaling.sendAnswer(answer);
      this.state = 'MATCHED';
    } catch (error) {
      console.error('Failed to handle offer:', error);
      this.showError('Failed to connect. Please try again.');
      this.leave();
    }
  }

  async handleAnswer(message) {
    try {
      await this.webrtc.handleAnswer(message.data);
      this.state = 'MATCHED';
    } catch (error) {
      console.error('Failed to handle answer:', error);
    }
  }

  handleConnectionState(state) {
    if (state === 'connected') this.markCallLive();
  }

  markCallLive() {
    if (this.leaving) return;
    if (this.state !== 'MATCHED' && this.state !== 'CONNECTED') return;
    if (this.mediaLive && this.screen === 'active') return;
    this.mediaLive = true;
    this.enterCallUI();
  }

  handleIceCandidate(message) {
    this.webrtc.addIceCandidate(message.data);
  }

  handlePeerLeft() {
    this.returnToQueue('Your match left. Finding someone new.', { rejoin: false });
  }

  handlePeerReconnecting() {
    this.peerReconnectingAt = this.clock.now();
    this.state = 'RECONNECTING';
    this.stopRemoteAudio();
    this.showError('Trying to reconnect...');
  }

  async handleSessionResume() {
    const wasReconnecting = this.state === 'RECONNECTING';
    const now = this.clock.now();
    const inGracePeriod = this.peerReconnectingAt ? now - this.peerReconnectingAt < this.reconnectGracePeriodMs : false;

    if (this.reconnectFallbackTimer) {
      this.timers.clearTimeout(this.reconnectFallbackTimer);
      this.reconnectFallbackTimer = null;
    }

    this.storage.removeItem(PREV_SESSION_KEY);

    try {
      if (!this.webrtc.pc) {
        await this.webrtc.initLocalStream();
        this.applyMuteStateToStream();
        this.webrtc.addLocalTracks();
        const offer = await this.webrtc.createOffer();
        this.signaling.sendOffer(offer);
        this.state = 'MATCHED';
      } else {
        await this.webrtc.restartIce();
        this.state = 'CONNECTED';
        if (!wasReconnecting || !inGracePeriod) this.markCallLive();
      }
    } catch (error) {
      this.showError('Failed to reconnect. Please try again.');
      this.leave();
    }
  }

  handleTimeout() {
    this.returnToQueue('Peer did not reconnect. Finding a new match.', { rejoin: false });
  }

  handleDisconnect() {
    if (this.leaving || this.state === 'IDLE' || this.screen === 'home') return;
    if (this.state === 'MATCHED' || this.state === 'CONNECTED' || this.state === 'RECONNECTING') {
      this.showError('Connection lost. Attempting to reconnect...');
    }
  }

  returnToQueue(message, { rejoin = false } = {}) {
    this.clearMatchingTimers();
    this.clearCallTimers();
    this.stopRemoteAudio();
    this.webrtc.close();
    this.resetMute(false);
    this.mediaLive = false;
    this.callStartedAt = null;
    this.callSeconds = 0;
    this.skipArmed = false;
    this.state = 'QUEUED';
    this.screen = 'matching';
    this.matchPhase = 'searching';
    this.showError(message);
    this.render();
    if (rejoin) this.rejoinQueue();
  }

  rejoinQueue() {
    if (this.signaling.isOpen?.() || (this.signaling.ws && this.signaling.ws.readyState === 1)) {
      this.signaling.sessionId = this.sessionId;
      this.signaling.joinQueue();
      return;
    }
    this.joinQueue();
  }

  handleNewMatchTap() {
    if (!this.skipArmed) {
      this.skipArmed = true;
      this.render();
      this.timers.clearTimeout(this.skipResetTimer);
      this.skipResetTimer = this.timers.setTimeout(() => {
        this.skipArmed = false;
        this.render();
      }, 2000);
      return;
    }

    this.timers.clearTimeout(this.skipResetTimer);
    this.skipArmed = false;
    this.render();
    this.timers.setTimeout(() => this.findNewMatch(), 300);
  }

  async findNewMatch() {
    this.webrtc.close();
    this.stopRemoteAudio();
    this.resetMute(false);
    this.callStartedAt = null;
    this.callSeconds = 0;
    this.clearCallTimers();
    this.clearAllSessionStorage();
    this.state = 'QUEUED';
    this.screen = 'matching';
    this.matchPhase = 'searching';
    this.render();

    if (this.signaling.ws) {
      this.signaling.leaveSession();
    } else {
      await this.joinQueue();
    }
  }

  leave() {
    this.leaving = true;
    this.clearMatchingTimers();
    this.clearCallTimers();
    this.mediaLive = false;
    if (this.state === 'RECONNECTING') {
      this.signaling.cancelReconnect();
    } else {
      this.signaling.leave();
    }
    this.webrtc.close();
    this.stopRemoteAudio();
    this.resetToHome();
    this.leaving = false;
  }

  enterCallUI() {
    this.clearMatchingTimers();
    const alreadyLive = this.screen === 'active' && this.callStartedAt;
    this.state = 'CONNECTED';
    this.screen = 'active';
    this.matchPhase = 'found';
    this.mediaLive = true;
    if (!alreadyLive) {
      this.callStartedAt = this.clock.now();
      this.callSeconds = 0;
      this.startCallTimer();
    }
    this.render();
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    this.applyMuteStateToStream();
    if (this.isMuted) this.isSpeaking = false;

    const button = this.root.querySelector('[data-action="mute"]');
    if (!button) {
      this.render();
      return;
    }

    button.classList.toggle('muted', this.isMuted);
    const icon = button.querySelector('.control-icon');
    const label = button.querySelector('[data-mute-label]');
    if (icon) icon.textContent = this.isMuted ? '🔇' : '🎙';
    if (label) label.textContent = this.isMuted ? 'Unmute' : 'Mute';
    this.updateActiveMotion();
  }

  toggleTheme() {
    this.isDark = !this.isDark;
    this.render();
  }

  startCallTimer() {
    this.timers.clearInterval(this.callTimer);
    this.callTimer = this.timers.setInterval(() => {
      if (!this.callStartedAt || this.screen !== 'active') return;
      this.callSeconds = Math.floor((this.clock.now() - this.callStartedAt) / 1000);
      this.updateActiveMotion();
    }, 1000);
  }

  updateActiveMotion() {
    if (this.screen !== 'active') return;

    const speaking = this.isSpeaking && !this.isMuted;
    const orb = this.root.querySelector('.orb-wrap');
    const bars = this.root.querySelector('.wave-bars');
    const timer = this.root.querySelector('.timer-chip');

    if (orb) orb.classList.toggle('speaking', speaking);
    if (bars) bars.classList.toggle('active', speaking);
    if (timer) timer.textContent = this.formatTime(this.callSeconds);
  }

  async tryReconnect() {
    const savedSessionId = this.storage.getItem(PREV_SESSION_KEY);
    if (!savedSessionId) return false;

    try {
      this.sessionId = savedSessionId;
      this.setSessionLock();
      this.state = 'RECONNECTING';
      this.screen = 'matching';
      this.matchPhase = 'found';
      this.render();

      await this.signaling.connect(this.sessionId);
      this.signaling.reconnect();

      this.reconnectFallbackTimer = this.timers.setTimeout(() => {
        if (this.state !== 'MATCHED' && this.state !== 'CONNECTED') {
          this.resetToHome();
        }
      }, 10000);

      return true;
    } catch (error) {
      console.error('[Reconnect] Failed:', error);
      this.resetToHome();
      return false;
    }
  }

  saveSessionForReconnect() {
    if (this.sessionId && (this.state === 'MATCHED' || this.state === 'CONNECTED' || this.state === 'RECONNECTING')) {
      this.storage.setItem(PREV_SESSION_KEY, this.sessionId);
    }
  }

  setSessionLock() {
    this.storage.setItem(STORAGE_KEY, this.sessionId);
  }

  clearAllSessionStorage() {
    this.storage.removeItem(STORAGE_KEY);
    this.storage.removeItem(PREV_SESSION_KEY);
  }

  resetToHome() {
    this.clearMatchingTimers();
    this.clearCallTimers();
    this.clearAllSessionStorage();
    this.resetMute(false);
    this.state = 'IDLE';
    this.screen = 'home';
    this.peerSessionId = null;
    this.callStartedAt = null;
    this.callSeconds = 0;
    this.isSpeaking = false;
    this.skipArmed = false;
    this.mediaLive = false;
    this.errorMessage = '';
    this.render();
  }

  resetMute(render = true) {
    this.isMuted = false;
    if (render) this.render();
  }

  clearMatchingTimers() {
    this.timers.clearTimeout(this.matchEnterTimer);
    this.matchEnterTimer = null;
  }

  clearCallTimers() {
    this.timers.clearInterval(this.callTimer);
    this.timers.clearTimeout(this.speakingTimer);
    this.callTimer = null;
    this.speakingTimer = null;
  }

  applyMuteStateToStream() {
    if (!this.webrtc.localStream) return;
    this.webrtc.localStream.getAudioTracks().forEach((track) => {
      track.enabled = !this.isMuted;
    });
  }

  stopRemoteAudio() {
    const audio = document.getElementById('remote-audio');
    if (audio) {
      audio.pause();
      audio.srcObject = null;
    }
  }

  playRemoteAudio(stream) {
    let audio = document.getElementById('remote-audio');
    if (!audio) {
      audio = document.createElement('audio');
      audio.id = 'remote-audio';
      audio.autoplay = true;
      audio.playsInline = true;
      audio.style.display = 'none';
      document.body.appendChild(audio);
    }

    audio.srcObject = stream;
    audio.play().catch(() => {
      this.showError('Tap the page once to hear your match.');
      const startAudio = () => {
        audio.play().catch(() => {});
        document.removeEventListener('click', startAudio);
        document.removeEventListener('touchstart', startAudio);
      };
      document.addEventListener('click', startAudio);
      document.addEventListener('touchstart', startAudio);
    });
  }

  updateActiveUsers(count) {
    const next = Number.isFinite(count) ? count : DEFAULT_ACTIVE_USERS;
    if (next === this.activeUsers) return;
    this.activeUsers = next;
    const label = this.formatCount();
    this.root.querySelectorAll('[data-online-count]').forEach((el) => {
      el.textContent = label;
    });
  }

  showError(message) {
    this.errorMessage = message;
    let toast = this.root.querySelector('.error-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'error-toast';
      this.root.appendChild(toast);
    }
    toast.textContent = message;
    this.timers.clearTimeout(this.errorTimer);
    this.errorTimer = this.timers.setTimeout(() => {
      this.errorMessage = '';
      toast.remove();
    }, 5000);
  }

  getGreeting() {
    const hour = new Date(this.clock.now()).getHours();
    if (hour < 12) return 'Good morning.';
    if (hour < 18) return 'Good afternoon.';
    return 'Good evening.';
  }

  formatCount() {
    return new Intl.NumberFormat('en-US').format(this.activeUsers || DEFAULT_ACTIVE_USERS);
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }

  matchDots() {
    return '<span class="dot-cycle">...</span>';
  }

  isDesktop() {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  escape(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }
}

export function bootstrap() {
  const app = new App();
  app.tryReconnect();
  return app;
}

if (!import.meta.env?.VITEST) {
  bootstrap();
}
